// src/components/global-content/ContentEditor.js - Simple text content editor (v1)
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const ContentEditor = ({ content, contentTypes, methodologies, defaultMethodologyId, onSave, onClose, loading }) => {
  const [formData, setFormData] = useState({
    key: '',
    title: '',
    description: '',
    content: '',
    contentType: 'prompt',
    methodologyId: defaultMethodologyId || 'real'
  });
  const [formErrors, setFormErrors] = useState({});
  const [contentStats, setContentStats] = useState({ chars: 0, words: 0, lines: 0 });
  
  const titleInputRef = useRef(null);

  const isEditing = !!content;

  useEffect(() => {
    if (content) {
      setFormData({
        key: content.key || '',
        title: content.title || '',
        description: content.description || '',
        content: content.content || '',
        contentType: content.contentType || 'prompt',
        methodologyId: content.methodologyId || 'real'
      });
    } else {
      // New content - focus on title
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [content]);

  useEffect(() => {
    updateContentStats();
  }, [formData.content]);

  const updateContentStats = () => {
    const text = formData.content || '';
    const chars = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const lines = text.split('\n').length;
    setContentStats({ chars, words, lines });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate key from title for new content
    if (name === 'title' && !isEditing) {
      const autoKey = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
      setFormData(prev => ({
        ...prev,
        key: autoKey
      }));
    }

    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.key.trim()) {
      errors.key = 'Key is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.key)) {
      errors.key = 'Key can only contain lowercase letters, numbers, and underscores';
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    }

    if (!formData.contentType) {
      errors.contentType = 'Content type is required';
    }

    if (!formData.methodologyId) {
      errors.methodologyId = 'Methodology is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        title: formData.title.trim(),
        key: formData.key.trim(),
        description: formData.description.trim() || undefined,
        content: formData.content.trim(),
        contentType: formData.contentType,
        methodologyId: formData.methodologyId
      };

      await onSave(submitData);
    } catch (err) {
      console.error('Error submitting form:', err);
      // Error is handled by parent component
    }
  };

  const handleKeyDown = (e) => {
    // Save with Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSubmit(e);
    }
    // Close with Escape
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const getContentTypeDescription = (type) => {
    const typeInfo = contentTypes.find(ct => ct.value === type);
    return typeInfo ? typeInfo.description : '';
  };

  return (
    <div className="modal-overlay" onKeyDown={handleKeyDown}>
      <div className="content-editor-modal">
        <div className="modal-header">
          <h3 className="modal-title">
            {isEditing ? `Edit Content: ${content.title}` : 'Create New Content'}
          </h3>
          <button 
            onClick={onClose} 
            className="modal-close" 
            disabled={loading}
            title="Close (Esc)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="content-editor-form">
          {/* Basic Information */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="title">
                  Title *
                </label>
                <input
                  ref={titleInputRef}
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.title ? 'error' : ''}`}
                  placeholder="Enter a descriptive title"
                  disabled={loading}
                  maxLength={200}
                  required
                />
                {formErrors.title && (
                  <p className="form-error">{formErrors.title}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="key">
                  Key *
                </label>
                <input
                  type="text"
                  id="key"
                  name="key"
                  value={formData.key}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.key ? 'error' : ''}`}
                  placeholder="unique_content_key"
                  disabled={loading || isEditing} // Can't change key when editing
                  maxLength={100}
                  required
                />
                {formErrors.key && (
                  <p className="form-error">{formErrors.key}</p>
                )}
                {!isEditing && (
                  <p className="form-help">
                    Unique identifier (auto-generated from title)
                  </p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="contentType">
                  Content Type *
                </label>
                <select
                  id="contentType"
                  name="contentType"
                  value={formData.contentType}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.contentType ? 'error' : ''}`}
                  disabled={loading}
                  required
                >
                  {contentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {formErrors.contentType && (
                  <p className="form-error">{formErrors.contentType}</p>
                )}
                <p className="form-help">
                  {getContentTypeDescription(formData.contentType)}
                </p>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="methodologyId">
                  Methodology *
                </label>
                <select
                  id="methodologyId"
                  name="methodologyId"
                  value={formData.methodologyId}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.methodologyId ? 'error' : ''}`}
                  disabled={loading || isEditing}
                  required
                >
                  {methodologies.map(m => (
                    <option key={m.id} value={m.id}>{m.displayName}</option>
                  ))}
                </select>
                {formErrors.methodologyId && (
                  <p className="form-error">{formErrors.methodologyId}</p>
                )}
                {isEditing && (
                  <p className="form-help">Methodology cannot be changed after creation.</p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Optional description of this content's purpose"
                disabled={loading}
                maxLength={500}
              />
            </div>
          </div>

          {/* Content Section */}
          <div className="form-section">
            <div className="form-group">
              <div className="content-header">
                <label className="form-label" htmlFor="content">
                  Content *
                </label>
                <div className="content-stats">
                  <span>{contentStats.chars} chars</span>
                  <span>{contentStats.words} words</span>
                  <span>{contentStats.lines} lines</span>
                </div>
              </div>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                className={`form-input content-textarea ${formErrors.content ? 'error' : ''}`}
                placeholder="Enter your content here..."
                disabled={loading}
                rows={20}
                style={{ minHeight: '400px', resize: 'vertical' }}
                required
              />
              {formErrors.content && (
                <p className="form-error">{formErrors.content}</p>
              )}
              <p className="form-help">
                Plain text content. Line breaks and spacing will be preserved.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <div className="flex flex-between">
              <div className="keyboard-shortcuts">
                <span className="text-xs text-gray-500">
                  {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+S to save • Esc to close
                </span>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !formData.title.trim() || !formData.key.trim() || !formData.content.trim()}
                >
                  {loading ? (
                    <>
                      <div className="loading-dots mr-2">
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                      </div>
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {isEditing ? 'Update Content' : 'Create Content'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

ContentEditor.propTypes = {
  content: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
    contentType: PropTypes.string,
    methodologyId: PropTypes.string
  }),
  contentTypes: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string
  })).isRequired,
  methodologies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired
  })).isRequired,
  defaultMethodologyId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

export default ContentEditor;